import { useMemo } from 'react';
import { AccommodationPlaceRoomBlockStatus, GuestTravelMapComponentFragment, useGuestEventHotelRoomBlocksQuery } from '@graphql/generated';
import { accommodationPlacesV2ToAccommodationCombined } from '../components/Accommodations/Accommodation.utils';
import { sortByFavorite, sortByOrderList } from '@shared/utils/array';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { isInIframe } from '@shared/utils/isInIframe';

export const useTravelMapData = (eventId: string, travelMapComponent: GuestTravelMapComponentFragment) => {
  const isPreviewing = isInIframe();
  const { origin, venueInfo } = useEventPlaces(isPreviewing);

  const { data: accommodationPlaceData, loading } = useGuestEventHotelRoomBlocksQuery({
    variables: {
      eventId,
    },
    // Use cache-and-network in preview mode to ensure accommodations reflect latest changes
    fetchPolicy: isPreviewing ? 'cache-and-network' : 'cache-first',
    batchMode: 'fast',
    notifyOnNetworkStatusChange: false,
  });

  const eventPhoto = accommodationPlaceData?.event?.photo?.url ?? '';

  const accommodations = useMemo(
    () =>
      accommodationPlaceData?.event?.accommodationPlacesV2
        ?.filter(accommodation => accommodation?.accommodationRoomBlock?.status !== AccommodationPlaceRoomBlockStatus?.cancelled)
        ?.map(accommodation => accommodationPlacesV2ToAccommodationCombined(accommodation, origin)) ?? [],
    [accommodationPlaceData?.event?.accommodationPlacesV2, origin],
  );

  const blockOrderingInitial = useMemo(
    () => (travelMapComponent && travelMapComponent.__typename === 'TravelMapComponent' ? travelMapComponent?.blockOrdering : []),
    [travelMapComponent],
  );

  const sortedHotelList = useMemo(() => sortByFavorite(sortByOrderList(accommodations, blockOrderingInitial ?? [])), [accommodations, blockOrderingInitial]);

  const blockOrdering = blockOrderingInitial && blockOrderingInitial?.length === sortedHotelList?.length ? blockOrderingInitial : sortedHotelList?.map(roomBlock => roomBlock.id);

  const showDefaultData = !loading && (!sortedHotelList || sortedHotelList.length === 0);

  return {
    loading,
    showDefaultData,
    venueInfo,
    eventTimezone: accommodationPlaceData?.event?.info.timezone,
    sortedHotelList: sortedHotelList,
    destination: venueInfo,
    eventPhoto,
    accommodations: sortedHotelList || [],
    blockOrdering,
  };
};
